import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { IRemunerationCommission } from 'interfaces/RemunerationCommission';
import {
  IMyKnownError,
  REQUEST_LIFE_CICLE,
  ICreateRequestPayload,
} from 'interfaces/requests';

import { get, post, patch, MICROSERVICES } from 'api';
import { AppDispatch } from 'store';
import { normalizeAndRemoveFields } from 'utils/files';
import { parseToInt } from 'utils/texts';
import { remunerationFieldsToRemove } from 'utils/remuneration';
import { openAlert } from 'modules/alertHandler';

import endpoints from './endpoints';
import { IRemunerationCommissionState } from './interfaces';
import { setSelectedRemunerationModeAction } from 'modules/remunerationModeNavigation/actions';
import { listRemunerationCommissionAction } from 'modules/remunerationCommissionList';
import { REMUNERATION_FORM_MODE } from 'modules/remunerationModeNavigation/constants';

export const showRemunerationCommissionAction = createAsyncThunk<
  IRemunerationCommission,
  string,
  { rejectValue: IMyKnownError }
>(
  'showRemunerationCommission',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const result = await get<IRemunerationCommission>(
        MICROSERVICES.STRATEGIES,
        {
          url: endpoints.showRemunerationCommission(payload),
        },
      );
      const flows = result.data.flows.map((flow) => ({
        ...flow,
        flowRanges: flow.flowRanges.map((flowRange, index) => ({
          ...flowRange,
          id: index.toString(),
        })),
      }));
      const remuneration = {
        ...result.data,
        id: result.data._id,
        flows: flows,
      };
      dispatch(
        setSelectedRemunerationModeAction(
          REMUNERATION_FORM_MODE.viewCommission,
        ),
      );
      return remuneration;
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: error.response.data.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const createRemunerationCommissionAction = createAsyncThunk<
  void,
  ICreateRequestPayload<IRemunerationCommission>,
  { rejectValue: IMyKnownError; dispatch: AppDispatch }
>(
  'createRemunerationCommission',
  async ({ item, resetFormSubmitting }, { rejectWithValue, dispatch }) => {
    const flows = item.flows.map((flow) => {
      if (flow.system === 'all') {
        delete flow.system;
      }
      return Object.assign({}, flow, {
        flowRanges: flow.flowRanges.map((flowRange) =>
          normalizeAndRemoveFields(Object.assign({}, flowRange), [
            'id',
            'beforeBreakpointValue',
          ]),
        ),
      });
    });
    const periodicityMonthSpecification = parseToInt(
      item.periodicityMonthSpecification,
    );
    const parsedRemunerationCommission = {
      ...normalizeAndRemoveFields(
        Object.assign({}, { ...item, periodicityMonthSpecification }),
        remunerationFieldsToRemove(item.periodicityType),
      ),
      flows,
    };
    try {
      await post<IRemunerationCommission>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createRemunerationsCommission(),
        data: parsedRemunerationCommission,
      });
      resetFormSubmitting();
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração criado com sucesso!',
          primaryButtonAction: setSelectedRemunerationModeAction(
            REMUNERATION_FORM_MODE.list,
          ),
        }),
      );
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Remuneração Comissão com esse nome.'
        : 'Erro para criar uma Comissão Fixa';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const inactivateRemunerationCommissionAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>(
  'inactivateRemunerationCommission',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await patch<unknown>(MICROSERVICES.STRATEGIES, {
        url: endpoints.inactivateRemunerationCommission(payload),
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração inativado com sucesso!',
          primaryButtonAction: listRemunerationCommissionAction({}),
        }),
      );
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: error.response.data.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

const initialState: IRemunerationCommissionState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  submitting: REQUEST_LIFE_CICLE.IDLE,
  remunerationCommission: {} as IRemunerationCommission,
  errorMessage: '',
};

const remunerationCommissionSlice = createSlice({
  name: 'remunerationCommission',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      showRemunerationCommissionAction.fulfilled,
      (state, action) => {
        state.errorMessage = '';
        state.remunerationCommission = action.payload;
        state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
        return state;
      },
    );
    builder.addCase(showRemunerationCommissionAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      showRemunerationCommissionAction.rejected,
      (state, action) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
    builder.addCase(createRemunerationCommissionAction.fulfilled, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(createRemunerationCommissionAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      createRemunerationCommissionAction.rejected,
      (state, action) => {
        state.submitting = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
    builder.addCase(inactivateRemunerationCommissionAction.fulfilled, () => {
      return initialState;
    });
    builder.addCase(inactivateRemunerationCommissionAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      inactivateRemunerationCommissionAction.rejected,
      (state, action) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
  },
});

const { reducer } = remunerationCommissionSlice;
export default reducer;
